import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import Spinner from "../../components/Shared/Spinner";
import MailIcon from "../../components/Icons/MailIcon";
import imagePlaceholder from "../../assets/ImgPlaceholder.svg";
import { getTransactionDetails } from "../../api/transactions/GetTransactionsDetails";
import { useUserDataStore } from "../../stores/user";

const TransactionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userData } = useUserDataStore();
  const basePath = userData?.role === "sales" ? "/sales/dashboard" : "/dashboard";
  const paymobOrderId = id || "";

  const { data: TransactionResponse, isLoading: isTransactionLoading } = useQuery({
    queryFn: () => getTransactionDetails(paymobOrderId),
    queryKey: ["transactionDetails", paymobOrderId],
    staleTime: 0,
    refetchInterval: 0,
  });

  const [activeTab, setActiveTab] = useState("details");

  // Fix TypeScript error by checking if response has data
  const transaction = TransactionResponse && 'data' in TransactionResponse ? TransactionResponse.data : undefined;
  const student = transaction?.student;
  const courses = transaction?.courses || [];
  const books = transaction?.books || [];
  const packages = transaction?.packages || [];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours() % 12 || 12;
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = date.getHours() >= 12 ? "PM" : "AM";

    return `${day}.${month}.${year} - ${hours}:${minutes} ${ampm}`;
  };

  const formatAmount = (amountCents: number, currency: string) => {
    const amount = amountCents / 100;
    return `${amount.toLocaleString()} ${currency}`;
  };

  const getStatusBadge = () => {
    if (transaction?.pending) {
      return (
        <span className="px-4 py-2 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-800">
          Pending
        </span>
      );
    }
    if (transaction?.success) {
      return (
        <span className="px-4 py-2 rounded-full text-sm font-semibold bg-green-100 text-green-800">
          Success
        </span>
      );
    }
    return (
      <span className="px-4 py-2 rounded-full text-sm font-semibold bg-red-100 text-red-800">
        Failed
      </span>
    );
  };

  if (isTransactionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="py-7 container">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Transaction not found</h1>
          <button
            onClick={() => navigate(`${basePath}/transactions`)}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Back to Transactions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-4 md:py-7 px-4 md:px-0 container">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-7">
        <h1 className="text-burntBlack font-bold text-2xl md:text-[32px]">Transaction Details</h1>
        <button
          onClick={() => navigate(`${basePath}/transactions`)}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 w-full sm:w-auto"
        >
          Back to Transactions
        </button>
      </div>

      <div className="p-4 md:p-8 lg:p-14 rounded-xl bg-white border border-black/15 shadow">
        {/* Student and Transaction Info */}
        <div className="pb-6 md:pb-9 border-b-2 border-black/15">
          <div className="flex flex-col md:flex-row gap-4 md:gap-7 items-center mb-6">
            <img
              src={student?.img_url || imagePlaceholder}
              alt="student profile"
              className="size-[100px] md:size-[120px] object-cover object-[75%_25%] rounded-full"
            />
            <div className="flex-1 text-center md:text-left">
              <h1 className="font-bold text-xl md:text-2xl lg:text-[32px] mb-3">
                {student?.firstName} {student?.lastName}
              </h1>
              <div className="flex flex-col gap-2 items-center md:items-start">
                <div className="flex gap-1.5 items-center w-fit py-2 px-3 bg-[#F3F4F6] rounded-full">
                  <MailIcon width={16} height={16} />
                  <p className="text-[#565E6C] text-sm">{student?.email}</p>
                </div>
                <div className="flex gap-1.5 items-center w-fit py-2 px-3 bg-blue-50 rounded-full">
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    className="text-blue-600"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  <p className="text-blue-700 text-sm font-medium">{student?.phone || "N/A"}</p>
                </div>
              </div>
            </div>
            <div className="w-full md:w-auto flex justify-center">{getStatusBadge()}</div>
          </div>

          {/* Transaction Summary Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-6 md:mt-8">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Transaction ID</p>
              <p className="font-semibold text-gray-900 text-xs font-mono break-all">
                {transaction.id}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Paymob Order ID</p>
              <p className="font-semibold text-gray-900">{transaction.paymob_order_id}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Amount</p>
              <p className="font-semibold text-gray-900 text-lg">
                {formatAmount(transaction.amount_cents, transaction.currency)}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Date</p>
              <p className="font-semibold text-gray-900 text-sm">
                {formatDate(transaction.createdAt)}
              </p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 md:gap-4 mt-6 w-full overflow-x-auto pb-2 scrollbar-hide">
          <button
            className={`py-2 px-3 md:px-4 h-12 md:h-14 text-base md:text-xl font-semibold rounded-xl min-w-fit md:w-full text-center transition-all duration-300 ${
              activeTab === "details"
                ? "bg-primary text-white"
                : "bg-gray-200 text-black"
            }`}
            onClick={() => setActiveTab("details")}
          >
            Payment Details
          </button>
          <button
            className={`py-2 px-3 md:px-4 h-12 md:h-14 text-base md:text-xl font-semibold rounded-xl min-w-fit md:w-full text-center transition-all duration-300 ${
              activeTab === "courses"
                ? "bg-primary text-white"
                : "bg-gray-200 text-black"
            }`}
            onClick={() => setActiveTab("courses")}
            disabled={courses.length === 0}
          >
            Courses ({courses.length})
          </button>
          <button
            className={`py-2 px-3 md:px-4 h-12 md:h-14 text-base md:text-xl font-semibold rounded-xl min-w-fit md:w-full text-center transition-all duration-300 ${
              activeTab === "books"
                ? "bg-primary text-white"
                : "bg-gray-200 text-black"
            }`}
            onClick={() => setActiveTab("books")}
            disabled={books.length === 0}
          >
            Books ({books.length})
          </button>
          <button
            className={`py-2 px-3 md:px-4 h-12 md:h-14 text-base md:text-xl font-semibold rounded-xl min-w-fit md:w-full text-center transition-all duration-300 ${
              activeTab === "packages"
                ? "bg-primary text-white"
                : "bg-gray-200 text-black"
            }`}
            onClick={() => setActiveTab("packages")}
            disabled={packages.length === 0}
          >
            Packages ({packages.length})
          </button>
        </div>

        {/* Tab Content */}
        <div className="pt-8">
          {/* Payment Details Tab */}
          {activeTab === "details" && (
            <div className="space-y-6">
              <h2 className="font-bold text-xl md:text-2xl mb-6">Payment Information</h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                <div className="border border-gray-200 p-4 md:p-6 rounded-lg">
                  <h3 className="font-semibold text-base md:text-lg mb-4 text-primary">
                    Payment Method
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm md:text-base">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium">{transaction.source_type}</span>
                    </div>
                    <div className="flex justify-between text-sm md:text-base">
                      <span className="text-gray-600">Card Type:</span>
                      <span className="font-medium">{transaction.source_sub_type}</span>
                    </div>
                    <div className="flex justify-between text-sm md:text-base">
                      <span className="text-gray-600">Card Last 4:</span>
                      <span className="font-medium font-mono">{transaction.source_pan}</span>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 p-4 md:p-6 rounded-lg">
                  <h3 className="font-semibold text-base md:text-lg mb-4 text-primary">
                    Transaction Status
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm md:text-base">
                      <span className="text-gray-600">Response Code:</span>
                      <span className="font-medium">{transaction.acq_response_code}</span>
                    </div>
                    <div className="flex justify-between text-sm md:text-base">
                      <span className="text-gray-600">Message:</span>
                      <span className="font-medium">{transaction.data_message}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm md:text-base">Paymob TXN ID:</span>
                      <span className="font-medium font-mono text-xs break-all">
                        {transaction.paymob_txn_id}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 p-4 md:p-6 rounded-lg">
                  <h3 className="font-semibold text-base md:text-lg mb-4 text-primary">
                    Student Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm md:text-base">
                      <span className="text-gray-600">Country:</span>
                      <span className="font-medium">{student?.country}</span>
                    </div>
                    <div className="flex justify-between text-sm md:text-base">
                      <span className="text-gray-600">Job Title:</span>
                      <span className="font-medium">
                        {student?.jobTitle === "null" ? "N/A" : student?.jobTitle}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm md:text-base">Member Since:</span>
                      <span className="font-medium text-xs md:text-sm">
                        {student && student.createdAt && formatDate(student?.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 p-4 md:p-6 rounded-lg">
                  <h3 className="font-semibold text-base md:text-lg mb-4 text-primary">
                    Purchase Summary
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm md:text-base">
                      <span className="text-gray-600">Total Items:</span>
                      <span className="font-medium">
                        {courses.length + books.length + packages.length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm md:text-base">Amount:</span>
                      <span className="font-bold text-base md:text-lg">
                        {formatAmount(transaction.amount_cents, transaction.currency)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Courses Tab */}
          {activeTab === "courses" && (
            <div className="space-y-6">
              <h2 className="font-bold text-xl md:text-2xl mb-6">Purchased Courses</h2>
              {courses.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No courses in this transaction</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {courses.map((course: any) => (
                    <div
                      key={course.id}
                      className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                    >
                      <img
                        src={course.img_url}
                        alt={course.name}
                        className="w-full h-48 object-cover"
                      />
                      <div className="p-5">
                        <h3 className="font-bold text-lg mb-2">{course.name}</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Duration:</span>
                            <span className="font-medium">{course.num_hours} hours</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Price (EGP):</span>
                            <span className="font-medium">
                              {parseInt(course.discountPrice_egp).toLocaleString()} EGP
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Price (USD):</span>
                            <span className="font-medium">
                              ${parseInt(course.discountPrice_usd).toLocaleString()}
                            </span>
                          </div>
                          {course.isTrending && (
                            <div className="mt-2">
                              <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded">
                                Trending
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Books Tab */}
          {activeTab === "books" && (
            <div className="space-y-6">
              <h2 className="font-bold text-xl md:text-2xl mb-6">Purchased Books</h2>
              {books.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No books in this transaction</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {books.map((book: any) => (
                    <div
                      key={book.id}
                      className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                    >
                      {book.img_url && (
                        <img
                          src={book.img_url}
                          alt={book.name}
                          className="w-full h-48 object-cover"
                        />
                      )}
                      <div className="p-5">
                        <h3 className="font-bold text-lg mb-2">{book.name}</h3>
                        {book.description && (
                          <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                            {book.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Packages Tab */}
          {activeTab === "packages" && (
            <div className="space-y-6">
              <h2 className="font-bold text-xl md:text-2xl mb-6">Purchased Packages</h2>
              {packages.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No packages in this transaction</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {packages.map((pkg: any) => (
                    <div
                      key={pkg.id}
                      className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                    >
                      {pkg.img_url && (
                        <img
                          src={pkg.img_url}
                          alt={pkg.name}
                          className="w-full h-48 object-cover"
                        />
                      )}
                      <div className="p-5">
                        <h3 className="font-bold text-lg mb-2">{pkg.name}</h3>
                        {pkg.description && (
                          <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                            {pkg.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionDetails;